import type { TextBlock } from "@prismicio/types-internal/lib/content"
import type { RichTextNodeType } from "@prismicio/types-internal/lib/customtypes"
import ListItem from "@tiptap/extension-list-item"
import type { Node as ProsemirrorNode, Schema } from "@tiptap/pm/model"

import { Paragraph } from "../extensions"
import { NodeUtils } from "../models"

const component = ListItem.extend({
  name: "listItem",
  content: "paragraph*",
  // we redefine the shortcuts to prevent nested list items for now.
  addKeyboardShortcuts() {
    return {
      Enter: () => this.editor.commands.splitListItem(this.name),
    }
  },
})

const converter = {
  fromPrismic(schema: Schema) {
    return function (list: TextBlock): ProsemirrorNode | undefined {
      const paragraph = Paragraph.converter.fromPrismic(schema)(list)
      if (!paragraph) return
      return schema.nodes[component.name]?.create({}, paragraph)
    }
  },

  toPrismic(
    node: ProsemirrorNode,
    nodeType:
      | typeof RichTextNodeType.list
      | typeof RichTextNodeType.orderedList,
  ): readonly TextBlock[] {
    return NodeUtils.children.reduce<readonly TextBlock[]>(
      node,
      (acc, child: ProsemirrorNode) => {
        const converted = Paragraph.converter.toPrismic(child)

        const modifiedBlock = (() => {
          if (converted instanceof Array) {
            return converted.map((c) => ({
              ...c,
              type: nodeType,
            }))
          } else {
            return converted ? [{ ...converted, type: nodeType }] : []
          }
        })()

        return [...acc, ...modifiedBlock]
      },
      [],
    )
  },
}

// eslint-disable-next-line import/no-default-export
export default {
  component,
  converter,
  placeholder: "List",
}

export function mergeLists(
  previousNode: ProsemirrorNode,
  newNode: ProsemirrorNode,
): ProsemirrorNode | undefined {
  return previousNode.copy(previousNode.content.append(newNode.content))
}
